import { Loader2, FileSearch, Brain, Calculator, FileText } from "lucide-react";

interface ProcessingStatusProps {
  progress: number;
  currentStep: string;
}

const STEPS = [
  { icon: FileSearch, label: "Document Analysis", threshold: 25 },
  { icon: Brain, label: "AI Processing", threshold: 50 },
  { icon: Calculator, label: "Cost Estimation", threshold: 75 },
  { icon: FileText, label: "Report Generation", threshold: 100 },
];

export function ProcessingStatus({
  progress,
  currentStep,
}: ProcessingStatusProps) {
  return (
    <div className="card-elevated p-8 max-w-2xl mx-auto animate-fade-in">
      {/* Animated Logo */}
      <div className="relative flex items-center justify-center mb-8">
        <div className="absolute w-24 h-24 bg-blue-100 rounded-full animate-pulse-soft" />
        <div className="absolute w-16 h-16 bg-blue-200 rounded-full animate-pulse-soft animation-delay-200" />
        <div className="relative w-12 h-12 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/30">
          <Loader2 className="w-6 h-6 text-white animate-spin" />
        </div>
      </div>

      <h2 className="text-2xl font-bold text-slate-900 mb-2 text-center">
        Analyzing Your Case
      </h2>
      
      <p className="text-slate-600 mb-8 text-center">
        {currentStep}
      </p>

      {/* Progress Steps */}
      <div className="grid grid-cols-4 gap-2 mb-6">
        {STEPS.map((step, index) => {
          const Icon = step.icon;
          const isActive = progress >= step.threshold - 25 && progress < step.threshold;
          const isComplete = progress >= step.threshold;
          
          return (
            <div
              key={step.label}
              className={`flex flex-col items-center p-3 rounded-xl transition-all duration-300 ${
                isComplete
                  ? "bg-emerald-50 text-emerald-600"
                  : isActive
                  ? "bg-blue-50 text-blue-600"
                  : "bg-slate-50 text-slate-400"
              }`}
            >
              <Icon
                className={`w-5 h-5 mb-1.5 ${
                  isActive ? "animate-bounce-soft" : ""
                }`}
              />
              <span className="text-xs font-medium text-center leading-tight">
                {step.label}
              </span>
            </div>
          );
        })}
      </div>

      {/* Progress Bar */}
      <div className="relative w-full h-2 bg-slate-100 rounded-full overflow-hidden mb-3">
        <div
          className="absolute h-full bg-gradient-to-r from-blue-500 via-indigo-500 to-violet-500 rounded-full transition-all duration-700 ease-out"
          style={{ width: `${progress}%` }}
        />
        <div
          className="absolute h-full w-20 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer"
          style={{
            left: `${Math.max(0, progress - 20)}%`,
            animation: "shimmer 1.5s infinite",
          }}
        />
      </div>

      <div className="flex items-center justify-between text-sm">
        <span className="text-slate-500">Processing...</span>
        <span className="font-semibold text-slate-900">{progress}%</span>
      </div>

      <div className="mt-8 p-4 bg-slate-50 rounded-xl">
        <p className="text-sm text-slate-600 text-center">
          This may take a few minutes depending on the complexity of your case.
          <br />
          <span className="text-slate-500">Please don't close this window.</span>
        </p>
      </div>

      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(400%); }
        }
        .animation-delay-200 {
          animation-delay: 200ms;
        }
      `}</style>
    </div>
  );
}
